/**
 * Curso e série pretendidos a partir da data de nascimento.
 *
 * A régua é a **idade completa em 31 de março do ano letivo** — a mesma data de
 * corte que a legislação usa para o 1º ano do Fundamental ("6 anos completos
 * até 31/03"). Aplicada a todos os níveis, ela vira uma escada limpa: um nível
 * por ano de idade, do Berçário I (0) à 3ª Série EM (17).
 *
 * O ano letivo **não** é o ano de hoje. Uma visita feita em agosto de 2026
 * quase sempre é matrícula para 2027, e a diferença de um ano muda a série
 * inteira. Por isso quem decide é o formulário, que mostra o ano usado.
 */

/** Um degrau da escada. O índice do array é a idade em 31/03. */
const ESCADA: ReadonlyArray<{ curso: string; serie: string }> = [
  { curso: "Educação Infantil", serie: "Berçário I" }, //  0
  { curso: "Educação Infantil", serie: "Infantil 1" }, //  1
  { curso: "Educação Infantil", serie: "Infantil 2" }, //  2
  { curso: "Educação Infantil", serie: "Infantil 3" }, //  3
  { curso: "Educação Infantil", serie: "Infantil 4" }, //  4
  { curso: "Educação Infantil", serie: "Infantil 5" }, //  5  (matrícula obrigatória)
  { curso: "Ensino Fundamental I", serie: "1º Ano" }, //   6
  { curso: "Ensino Fundamental I", serie: "2º Ano" }, //   7
  { curso: "Ensino Fundamental I", serie: "3º Ano" }, //   8
  { curso: "Ensino Fundamental I", serie: "4º Ano" }, //   9
  { curso: "Ensino Fundamental I", serie: "5º Ano" }, //  10
  { curso: "Ensino Fundamental II", serie: "6º Ano" }, // 11
  { curso: "Ensino Fundamental II", serie: "7º Ano" }, // 12
  { curso: "Ensino Fundamental II", serie: "8º Ano" }, // 13
  { curso: "Ensino Fundamental II", serie: "9º Ano" }, // 14
  { curso: "Ensino Médio", serie: "1ª Série EM" }, //     15
  { curso: "Ensino Médio", serie: "2ª Série EM" }, //     16
  { curso: "Ensino Médio", serie: "3ª Série EM" }, //     17
];

export interface Escolaridade {
  idade: number;
  curso: string;
  serie: string;
}

/** Quebra "AAAA-MM-DD" sem passar por `new Date` — evita o fuso comer um dia. */
function partes(iso: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!m) return null;
  const a = Number(m[1]);
  const mes = Number(m[2]);
  const dia = Number(m[3]);
  if (a < 1900 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
  return [a, mes, dia];
}

/**
 * Idade completa em 31/03 do ano letivo.
 * Quem faz aniversário em abril ou depois ainda não completou — conta um a menos.
 */
export function idadeNoCorte(nascimento: string, anoLetivo: number): number | null {
  const p = partes(nascimento);
  if (!p || !Number.isFinite(anoLetivo)) return null;
  const [ano, mes] = p;
  return anoLetivo - ano - (mes > 3 ? 1 : 0);
}

/** Curso e série sugeridos, ou `null` fora da faixa atendida (0 a 17 anos). */
export function sugerirEscolaridade(
  nascimento: string,
  anoLetivo: number,
): Escolaridade | null {
  const idade = idadeNoCorte(nascimento, anoLetivo);
  if (idade === null || idade < 0 || idade >= ESCADA.length) return null;
  return { idade, ...ESCADA[idade] };
}

/**
 * Ano letivo provável de uma visita.
 *
 * A campanha de matrícula do ano seguinte começa no meio do ano, então visita
 * de julho em diante é, por padrão, para o ano que vem. Antes disso, assume o
 * ano corrente (transferência no meio do ano). É só um palpite — o formulário
 * deixa trocar.
 */
export function anoLetivoSugerido(dataVisita: string | Date): number {
  const d = dataVisita instanceof Date ? dataVisita : new Date(dataVisita);
  if (Number.isNaN(d.getTime())) return new Date().getFullYear();
  return d.getMonth() + 1 >= 7 ? d.getFullYear() + 1 : d.getFullYear();
}

/** Idade de hoje, só para exibição ("9 anos"). */
export function idadeHoje(nascimento: string, hoje = new Date()): number | null {
  const p = partes(nascimento);
  if (!p) return null;
  const [ano, mes, dia] = p;
  let idade = hoje.getFullYear() - ano;
  const m = hoje.getMonth() + 1;
  if (m < mes || (m === mes && hoje.getDate() < dia)) idade -= 1;
  return idade < 0 ? null : idade;
}
